"use client";

import { useEffect, useState, useMemo } from "react";
import { getLenis } from "@/components/SmoothScrollProvider";
import { PROJECT_INFO } from "@/types/projectConsts";
import { cn } from "@/utils/cn";
import { Search } from "lucide-react";
import { usePathname, useRouter, useParams } from "next/navigation";
import RegisterModal from "@/components/RegisterModal";

const SECTIONS = [
  { id: "01", label: "Trang chủ", href: "#banner-anchor" },
  { id: "02", label: "Giới thiệu", href: "#gioi-thieu" },
  { id: "03", label: "Vị trí", href: "#vi-tri" },
  { id: "04", label: "Tiện ích", href: "#tien-ich" },
  { id: "05", label: "Mặt bằng", href: "#floor" },
  { id: "06", label: "Chính sách", href: "#chinh-sach-ban-hang" },
  { id: "07", label: "Tiến độ", href: "#process" },
  { id: "08", label: "Tin tức", href: "#tin-tuc" },
];

export default function ScrollHeader({
  fb,
  mail,
  alwaysVisible = true,
  visibleSections,
  apptOptions,
  formTitle,
  formSubtitle,
  formLayoutTypeTitle,
  logoUrl,
}: {
  fb: string;
  mail: string;
  alwaysVisible?: boolean;
  visibleSections?: Record<string, boolean>;
  apptOptions?: { label: string; value: string }[];
  formTitle?: string;
  formSubtitle?: string;
  formLayoutTypeTitle?: string;
  logoUrl?: string;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const params = useParams();
  const locale = params?.locale || "vi";

  // Check if we are on the homepage
  const isHome = pathname === "/" || pathname === `/${locale}`;

  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpenMobile, setIsOpenMobile] = useState(false);
  const [activeSection, setActiveSection] = useState("#banner-anchor");
  const [openRegister, setOpenRegister] = useState(false);

  const activeSections = useMemo(() => {
    if (!isHome) {
      return [
        { id: "01", label: "Trang chủ", href: "/" },
        { id: "08", label: "Tin tức", href: "/tin-tuc" },
      ];
    }
    return SECTIONS.filter(
      (s) => !visibleSections || visibleSections[s.href] !== false,
    );
  }, [isHome, visibleSections]);

  const currentActive = useMemo(() => {
    if (!isHome) {
      if (pathname.includes("/tin-tuc")) {
        return "/tin-tuc";
      }
      return "/";
    }
    return activeSection;
  }, [isHome, pathname, activeSection]);

  // Track scroll positions for active sections and header background changes
  useEffect(() => {
    const handleScroll = () => {
      // Toggle background style based on scroll
      setIsScrolled(window.scrollY > 20);

      if (!isHome) return;

      const scrollPosition = window.scrollY + 120;

      // Top scroll position -> active home
      if (window.scrollY < 200) {
        setActiveSection("#banner-anchor");
        return;
      }

      for (const item of activeSections) {
        if (item.href === "#banner-anchor") continue;
        const el = document.querySelector(item.href);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY;
          const height = el.clientHeight;
          if (
            scrollPosition >= top - 20 &&
            scrollPosition < top + height - 20
          ) {
            setActiveSection(item.href);
            return;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHome, activeSections]);

  const handleMenuClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setIsOpenMobile(false);

    if (isHome) {
      const lenis = getLenis();
      const el = document.querySelector(href);
      if (el) {
        if (lenis) {
          lenis.scrollTo(href, { offset: -72 });
        } else {
          el.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      } else {
        // Fallback to scrolling to the main lead form
        const formEl = document.getElementById("dang-ky-bao-gia");
        if (formEl) {
          if (lenis) {
            lenis.scrollTo("#dang-ky-bao-gia", { offset: -72 });
          } else {
            formEl.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }
      }
    } else {
      if (href === "/") {
        router.push(`/${locale}`);
      } else if (href === "/tin-tuc") {
        router.push(`/${locale}/tin-tuc`);
      } else {
        router.push(`/${locale}${href}`);
      }
    }
  };

  const handleLogoClick = () => {
    if (isHome) {
      const lenis = getLenis();
      if (lenis) {
        lenis.scrollTo("#banner-anchor");
      } else {
        const el = document.getElementById("banner-anchor");
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
    } else {
      router.push(`/${locale}`);
    }
  };

  return (
    <>
      {/* HEADER: STICKY AT THE TOP */}
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-header transition-all duration-300 w-full",
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-md py-3 border-b border-black/5"
            : "bg-transparent py-5 border-b border-transparent",
        )}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between relative">
          {/* LEFT: LOGO */}
          <div
            onClick={handleLogoClick}
            className="flex items-center cursor-pointer transition hover:opacity-90 flex-shrink-0"
          >
            <img
              src={logoUrl || "/logos/logo_transparent.png"}
              alt="Dragon Home"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </div>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
            {activeSections.map((section) => {
              const isActive = currentActive === section.href;
              return (
                <a
                  key={section.id}
                  href={section.href}
                  onClick={(e) => handleMenuClick(e, section.href)}
                  className={cn(
                    "text-[11px] xl:text-[12px] font-black uppercase tracking-wider transition-all duration-300",
                    isActive
                      ? "text-primary border-b-2 border-primary pb-1"
                      : "text-primary-dark hover:text-primary",
                  )}
                >
                  {section.label}
                </a>
              );
            })}
          </nav>

          {/* RIGHT ACTION BUTTONS (Desktop) */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-5 flex-shrink-0">
            {/* Báo giá Button */}
            <button
              onClick={() => setOpenRegister(true)}
              className="flex items-center gap-2 group cursor-pointer bg-primary hover:bg-primaryDark text-white px-4 py-1.5 rounded-full border border-primary hover:border-primaryDark transition-all duration-300 focus:outline-none shadow-md"
            >
              <svg
                className="w-3.5 h-3.5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <span className="text-[10px] font-black uppercase tracking-wider text-white">
                Báo giá
              </span>
            </button>

            {/* Zalo/Hotline Phone Call */}
            <a
              href={`tel:${PROJECT_INFO.hotline.replace(/\s+/g, "")}`}
              className="flex items-center gap-2 group cursor-pointer"
            >
              <div className="w-8 h-8 rounded-full border border-primary flex items-center justify-center text-primary transition-all duration-300 group-hover:bg-primary group-hover:text-white">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <span className="text-[12px] font-black tracking-wider text-primary font-montserrat transition group-hover:opacity-85">
                {PROJECT_INFO.hotline.replace(/\s+/g, ".")}
              </span>
            </a>
          </div>

          {/* MOBILE CONTROLS & HAMBURGER */}
          <div className="lg:hidden flex items-center gap-4 ml-auto">
            {/* Direct Phone button for mobile */}
            <a
              href={`tel:${PROJECT_INFO.hotline.replace(/\s+/g, "")}`}
              className="p-2 border border-primary text-primary rounded-full"
              aria-label="Call Hotline"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </a>

            {/* Hamburger button */}
            <button
              type="button"
              onClick={() => setIsOpenMobile(true)}
              className="p-1 text-secondary-dark hover:text-primary transition-colors"
              aria-label="Toggle Menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE DRAWER */}
      {isOpenMobile && (
        <div className="fixed inset-0 z-above_header bg-black/60 backdrop-blur-sm lg:hidden">
          <div className="absolute right-0 top-0 h-full w-[280px] bg-secondary-dark p-6 text-white shadow-2xl flex flex-col gap-6 animate-slide-in">
            <div className="flex justify-between items-center border-b border-white/10 pb-4">
              <img
                src={logoUrl || "/logos/logo_transparent.png"}
                alt="Logo"
                className="h-10 object-contain"
              />
              <button
                type="button"
                onClick={() => setIsOpenMobile(false)}
                className="text-white text-xl p-2 hover:opacity-80 transition"
              >
                ✕
              </button>
            </div>

            <nav className="flex flex-col gap-3 overflow-y-auto max-h-[60vh] pr-2">
              {activeSections.map((item) => {
                const isActive = currentActive === item.href;
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => handleMenuClick(e, item.href)}
                    className={cn(
                      "text-sm py-2 border-b border-white/5 font-bold uppercase tracking-wider transition duration-200",
                      isActive
                        ? "text-primary"
                        : "text-white/80 hover:text-primary",
                    )}
                  >
                    {item.label}
                  </a>
                );
              })}
            </nav>

            <div className="mt-auto flex flex-col gap-3 border-t border-white/10 pt-6">
              <button
                onClick={() => {
                  setIsOpenMobile(false);
                  setOpenRegister(true);
                }}
                className="flex items-center justify-center gap-2 bg-primary hover:bg-primaryDark text-white py-3 rounded-none font-semibold text-xs uppercase tracking-wider transition shadow-md"
              >
                <span>Nhận báo giá</span>
              </button>

              <a
                href={`tel:${PROJECT_INFO.hotline.replace(/\s+/g, "")}`}
                className="flex items-center justify-center gap-2 border border-white/20 hover:bg-white/10 text-white py-3 rounded-none font-semibold text-xs uppercase tracking-wider transition shadow-md"
              >
                <span>
                  Gọi ngay: {PROJECT_INFO.hotline.replace(/\s+/g, ".")}
                </span>
              </a>
            </div>
          </div>
        </div>
      )}

      <RegisterModal
        open={openRegister}
        onClose={() => setOpenRegister(false)}
        apptOptions={apptOptions}
        formTitle={formTitle}
        formSubtitle={formSubtitle}
        formLayoutTypeTitle={formLayoutTypeTitle}
      />
    </>
  );
}
